import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Button, Table, Empty, Dropdown } from 'antd'
import classNames from 'classnames'
import { useIsMobile, timeFormat, formatBytes } from 'utils'
import { LabelContent } from 'utils/ui'
import { ViewManager, SRC_TYPE, createUserData } from 'pages/records/components/helper'
import useShareExtensionRequest from 'pages/records/components/ExtensionRequest'
import { getRsKeyObjFromResource } from 'services/vaultV2/resourceKey'
import GroupService from 'api/Group'
import getTableInfo from 'pages/records/tableInfo/sharedUsers2'
import { getPlanFileSizeLimit, hasPlanOversizedFileInItems, isTotpValueReadOnly } from 'utils/plan'

import styles from 'pages/records/components/CardView/style.module.scss'
import favoriteIcon from '@/assets/images/icon-favorite.svg'
import favoriteFillIcon from '@/assets/images/icon-favorite-fill.svg'
import expiredIcon from '@/assets/images/icon-access-expired.svg'
import menuIcon from '@/assets/images/button-menu-h.svg'

export const getLatestLeakedDate = (individual, locale) => {
  const date = individual

  const format = locale === 'ko-KR' ? 'YYYY년 MMMM' : 'MMMM YYYY'

  return timeFormat(date, format)
}

// RSA 키 객체 생성 함수
// 🛑 본문은 `services/vaultV2/resourceKey.js` 로 옮겼다 (PR 7-a Task A0) — 이 파일의 17개 import
// 체인이 `redux/user/sagas.js` 까지 딸려 오면 안 되기 때문이다. re-export 를 남기는 이유는
// 기존 호출부 3곳(`utils/importExport/bulkExport.js` · `totpDuplicateCheck.js` ·
// `pages/records/components/TotpCodeChip.js`)과 이 경로를 통째로 `jest.mock` 하는 테스트 4개가
// 그대로 동작해야 하기 때문이다. 경로를 바꾸면 그 mock 들이 조용히 실물을 부른다.
export { getRsKeyObjFromResource }

const RecordCardContent = ({
  individual,
  domainImageElem,
  labelOpen,
  setLabelOpen,
  handleCopy,
  showCopyResult,
  handleFavoriteSubmit,
  reloadRecord,
  reload,
  resourceId,
  orgChartData,
  styles0 = {},
  onMenuClick,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile(1029)
  const [viewInstance, setViewInstance] = useState()
  const [shareList, setShareList] = useState([])
  const [groupMemberList, setGroupMemberList] = useState([])
  const messageRef = useRef('')

  const email = useSelector((state) => state.user.email)
  const handleShareAgain = useShareExtensionRequest(resourceId, t, messageRef)
  const showZeroDarkWeb = useSelector((state) => state.settings.showZeroDarkWeb)
  const locale = useSelector((state) => state.settings.locale)

  // 만료(431) 상태의 즐겨찾기 여부는 expiredData 로 실려 온다 (모바일 별 버튼도 만료 시 동작해야 함)
  const isFavorite = individual?.is_favorite ?? individual?.expiredData?.is_favorite
  const isExpired = individual != null && individual.expired === true

  const fetchUserListForGroups = async () => {
    try {
      const shareGroupList = individual?.share?.filter((item) => item.type === 'group')

      if (shareGroupList && shareGroupList.length > 0) {
        const result = await Promise.all(
          shareGroupList.map(async (group) => {
            const response = await GroupService.get({ id: group.id })

            return {
              id: group.id,
              member: response.data,
            }
          }),
        )

        setGroupMemberList(result)
      }
    } catch (error) {
      console.error('error: ', error)
    }
  }

  useEffect(() => {
    const hasGroupShare = individual?.share?.some((item) => item.type === 'group')
    if (hasGroupShare) {
      fetchUserListForGroups()
    }
  }, [])

  // ViewManager 및 공유 목록 설정
  useEffect(() => {
    if (individual && !isExpired) {
      // 그룹 멤버 목록 조회 (그룹 공유가 있는 경우에만)

      // ViewManager 설정
      getRsKeyObjFromResource(individual).then((rsKeyObj) => {
        const viewMgr = new ViewManager(
          rsKeyObj,
          individual.items,
          setViewInstance,
          showCopyResult,
          individual.title,
          individual.id,
          null,
          individual,
        )
        console.log(' --- viewMgr ---', viewMgr)
      })

      // 공유 목록 생성
      const list =
        individual.share
          ?.filter((item) => item.id !== individual.owner.id)
          .map((item) => {
            if (item.type === 'group') {
              return createUserData(SRC_TYPE.SHARED_GROUP, item, orgChartData)
            }
            return createUserData(SRC_TYPE.SHARED_USER, item, orgChartData)
          }) || []

      const ownerData = createUserData(SRC_TYPE.OWNER, individual.owner, orgChartData)
      const filterHash = (arr) =>
        arr.filter((item) => !(typeof item.email === 'string' && item.email.includes('#')))
      const shareListData =
        individual.owner.email === email ? filterHash(list) : filterHash([ownerData, ...list])

      setShareList(shareListData)
    } else {
      setShareList([])
      setGroupMemberList([])
    }
  }, [individual, isExpired, showCopyResult])

  // 모바일 렌더링 처리
  const isMobileRender = (content) => {
    if (isMobile) {
      return content
    }
    return null
  }

  // 레코드 시간 렌더링 처리
  const recordTime = () => {
    if (resourceId != null && individual?.updated_at != null) {
      return t('appConfig.field.updatedAtFormat', {
        date: timeFormat(individual?.updated_at, 'lll'),
        name: individual?.updater?.name || null,
      })
    }
    return null
  }

  // 만료 시간 렌더링 처리
  const expiredText = () => {
    const expiredAt = isExpired
      ? individual?.expiredData?.resource_key?.expired_at
      : individual?.resource_key?.expired_at

    return expiredAt
      ? t('record.label.accessableDate', { date: timeFormat(expiredAt, 'LLL') })
      : null
  }

  // 메뉴 렌더링 함수
  const renderMenu = onMenuClick
    ? (record) => {
        const { role: userRole } = record.originData || {}
        const isOwnerRole =
          userRole === 'owner' ||
          record.sourceType === SRC_TYPE.OWNER ||
          record.sourceType === SRC_TYPE.GROUP_OWNER

        // Owner는 메뉴 아이콘 숨김
        if (isOwnerRole) {
          return null
        }

        // 그룹 타입은 메뉴 아이콘 숨김
        if (record.type === 'group') {
          return null
        }

        // 만료된 레코드는 메뉴 아이콘 숨김
        if (record.expired === true) {
          return null
        }

        if (record.active === false || record.active === null) {
          return null
        }

        const menuItems = [
          {
            key: 'transfer',
            label: t('record.menu.transferOwnership'),
          },
        ]

        return (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: ({ key }) => onMenuClick(key, record),
            }}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName={classNames(styles0.transferOwnershipDropdown)}
          >
            <Button
              type="text"
              className={classNames(styles0.menuButton)}
              icon={<img src={menuIcon} alt="menu" />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )
      }
    : null

  // 테이블 정보 조회
  const tableInfo = getTableInfo(styles0, groupMemberList, null, null, true, renderMenu)
  const tableAttrs = {
    className: classNames(styles0.userTable2, 'smallTable'),
    onRow: tableInfo.onRow,
    pagination: false,
    showHeader: false,
    columns: tableInfo.columns,
    locale: { emptyText: null },
    dataSource: shareList,
    bordered: false,
    rowKey: (row) => {
      return row.id
    },
  }

  let content = null
  if (individual?.expired) {
    const emptyAttrs = {
      className: classNames(styles.accessExpired),
      image: expiredIcon,
      description: t('record.result.accessExpired', {
        email: individual.expiredData?.creator?.email,
      }),
    }

    content = (
      <React.Fragment>
        <Empty {...emptyAttrs} />
        <Button type="primary" onClick={handleShareAgain}>
          {t('record.button.shareAgain')}
        </Button>
      </React.Fragment>
    )
  } else {
    content = (
      <React.Fragment>
        {viewInstance}
        {individual?.items && shareList.length > 0 && (
          <div>
            <div className={classNames(styles.inputContainer)}>
              <span>{t('record.field.sharedWith')}</span>
              <Table {...tableAttrs} />
            </div>
          </div>
        )}
      </React.Fragment>
    )
  }

  const isLeakedRecord =
    individual?.is_leaked === true || individual?.expiredData?.is_leaked === true
  const leakedData = individual?.leaked_content || individual?.expiredData?.leaked_content

  // 다운그레이드 잔존 초과 첨부 안내(§4.7) — 제목보다 위에 빨간 문구로 표시.
  // 판정은 플랜 값(license.max_file_size_bytes) 그 자체 — 업로드 실효 상한과 분리(dev 확장 미적용).
  const hasPlanOversizedAttachment = hasPlanOversizedFileInItems(individual?.items)
  // Basic(JP·PAID) + totp 보유 = 그 필드 값만 수정 불가(레코드 편집·저장은 가능) — 필드가
  // 회색인 이유를 설명하는 유일한 자리다. 두 제약이 겹치면 두 줄이 함께 뜬다: 초과 첨부는
  // "삭제해야 저장됨"(필수 행동), 이 줄은 "값은 못 바꾸지만 조회·복사는 된다"(정보).
  const isTotpValueLocked = isTotpValueReadOnly(individual)

  return (
    <div className={classNames(styles.cardContents)}>
      {showZeroDarkWeb === true && isLeakedRecord && (
        <div className={classNames(styles.isLeaked)}>
          {t('dark-web.leakedAlert', {
            date: getLatestLeakedDate(leakedData, locale),
          })}
        </div>
      )}
      {hasPlanOversizedAttachment && (
        <div className={classNames(styles0.planLimitNotice)} role="alert">
          {t('record.attachment.planLimitExceededNotice', {
            limit: formatBytes(getPlanFileSizeLimit(), 0),
          })}
        </div>
      )}
      {isTotpValueLocked && (
        // 색은 초과 첨부 안내와 같은 빨강으로 통일(사용자 요청 2026-08-26) —
        // 등급 제한 안내가 회색이라 눈에 안 띈다는 피드백. 다만 **role 은 note 를 유지**한다:
        // alert 로 올리면 assertive 라이브 리전이 둘 겹쳐 스크린리더가 한쪽을 삼킨다.
        <div className={classNames(styles0.planInfoNotice)} role="note">
          {t('record.totp.valueReadOnlyNotice')}
        </div>
      )}
      <div>
        <div className={classNames(styles.titleContainer)}>
          {isMobileRender(<div>{domainImageElem}</div>)}
          <div
            className={classNames(styles.cardContentsTitle, 'disable-copy')}
            role="button"
            tabIndex="0"
            onKeyDown={() => {}}
            onClick={() => {
              handleCopy()
              showCopyResult()
            }}
          >
            {isExpired === true ? individual?.expiredData?.title : individual?.title}
          </div>
          {isMobileRender(
            <Button
              type="text"
              icon={
                <img src={isFavorite === true ? favoriteFillIcon : favoriteIcon} alt="favorite" />
              }
              className={classNames(styles.favoriteIcon)}
              onClick={handleFavoriteSubmit}
            />,
          )}
        </div>

        <span className={classNames(styles.recordTime)}>{recordTime()}</span>
        <span className={classNames(styles.recordTime)}>{expiredText()}</span>
        <LabelContent
          labelOpen={labelOpen}
          setLabelOpen={setLabelOpen}
          individual={individual?.categories != null ? individual : individual?.expiredData}
          reloadRecord={reloadRecord}
          deleteIcon
          reload={reload}
        />
      </div>
      {content}
    </div>
  )
}

export default RecordCardContent
